import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  CreditCard, Truck, MessageCircle, Check, X, Settings, Eye, EyeOff, 
  Save, AlertCircle, CheckCircle2, RefreshCw, ExternalLink, Info, Package,
  BarChart3, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';

// Integration Card Component
function IntegrationCard({ 
  title, 
  description, 
  icon: Icon, 
  iconColor,
  status, 
  children,
  onTest,
  testLoading,
  helpUrl
}) {
  return (
    <Card className="border-2 hover:border-gray-300 transition-colors">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl ${iconColor} flex items-center justify-center shadow-lg`}>
              <Icon className="w-7 h-7 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                {title}
                {status === 'active' && (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Active
                  </Badge>
                )}
                {status === 'configured' && (
                  <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Configured
                  </Badge>
                )}
                {status === 'inactive' && (
                  <Badge className="bg-gray-100 text-gray-600 border-gray-200">
                    Not Configured
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-1">{description}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {helpUrl && (
              <Button variant="ghost" size="sm" asChild>
                <a href={helpUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            )}
            {onTest && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onTest}
                disabled={testLoading}
              >
                {testLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Test
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {children}
      </CardContent>
    </Card>
  );
}

// Cashfree Integration Component
function CashfreeIntegration() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [formData, setFormData] = useState({
    app_id: '',
    secret_key: '',
    environment: 'test',
    is_active: true
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/payment-config`);
      const cashfreeConfig = response.data.find(c => c.provider === 'cashfree');
      if (cashfreeConfig) {
        setConfig(cashfreeConfig);
        setFormData({
          app_id: cashfreeConfig.app_id || '',
          secret_key: cashfreeConfig.secret_key || '',
          environment: cashfreeConfig.environment || 'test',
          is_active: cashfreeConfig.is_active !== false
        });
      }
    } catch (error) {
      console.error('Failed to fetch Cashfree config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.app_id || !formData.secret_key) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        provider: 'cashfree',
        ...formData
      };
      
      if (config?.id) {
        await axiosInstance.put(`${API_URL}/api/admin/payment-config/${config.id}`, payload);
      } else {
        await axiosInstance.post(`${API_URL}/api/admin/payment-config`, payload);
      }
      
      toast.success('Cashfree configuration saved!');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const getStatus = () => {
    if (!config) return 'inactive';
    if (config.is_active && config.app_id && config.secret_key) return 'active';
    if (config.app_id) return 'configured';
    return 'inactive';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <IntegrationCard
      title="Cashfree Payments"
      description="Accept payments via UPI, Cards, NetBanking, Wallets"
      icon={CreditCard}
      iconColor="bg-gradient-to-br from-blue-500 to-purple-600"
      status={getStatus()}
      helpUrl="https://docs.cashfree.com/docs/getting-started"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cashfree_app_id">App ID *</Label>
            <Input
              id="cashfree_app_id"
              value={formData.app_id}
              onChange={(e) => setFormData({...formData, app_id: e.target.value})}
              placeholder="Enter Cashfree App ID"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cashfree_secret">Secret Key *</Label>
            <div className="relative">
              <Input
                id="cashfree_secret"
                type={showSecret ? 'text' : 'password'}
                value={formData.secret_key}
                onChange={(e) => setFormData({...formData, secret_key: e.target.value})}
                placeholder="Enter Secret Key"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Environment</Label>
            <select
              value={formData.environment}
              onChange={(e) => setFormData({...formData, environment: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="test">Test (Sandbox)</option>
              <option value="production">Production (Live)</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <div className="flex items-center gap-3 h-10">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm font-medium">Enable Cashfree Payments</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
          <p className="text-sm text-gray-500">
            <Info className="w-4 h-4 inline mr-1" />
            Get credentials from <a href="https://merchant.cashfree.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Cashfree Dashboard</a>
          </p>
        </div>
      </div>
    </IntegrationCard>
  );
}

// Shiprocket Integration Component
function ShiprocketIntegration() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    is_active: true
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/integrations/shiprocket`);
      if (response.data) {
        setConfig(response.data);
        setFormData({
          email: response.data.email || '',
          password: response.data.password || '',
          is_active: response.data.is_active !== false
        });
      }
    } catch (error) {
      // Config doesn't exist yet, use env defaults
      setFormData({
        email: '',
        password: '',
        is_active: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      await axiosInstance.post(`${API_URL}/api/admin/integrations/shiprocket`, formData);
      toast.success('Shiprocket configuration saved!');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const response = await axiosInstance.post(`${API_URL}/api/admin/integrations/shiprocket/test`, formData);
      if (response.data.success) {
        toast.success('Shiprocket connection successful!');
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const getStatus = () => {
    if (!config) return 'inactive';
    if (config.is_active && config.email && config.password) return 'active';
    if (config.email) return 'configured';
    return 'inactive';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <IntegrationCard
      title="Shiprocket Logistics"
      description="Automated shipping, tracking, and delivery management"
      icon={Truck}
      iconColor="bg-gradient-to-br from-orange-500 to-red-500"
      status={getStatus()}
      onTest={handleTest}
      testLoading={testing}
      helpUrl="https://www.shiprocket.in/api/"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="shiprocket_email">Email *</Label>
            <Input
              id="shiprocket_email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="Enter Shiprocket login email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="shiprocket_password">Password *</Label>
            <div className="relative">
              <Input
                id="shiprocket_password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Enter Shiprocket password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Status</Label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium">Enable Shiprocket Integration</span>
            </label>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h4 className="font-semibold text-orange-800 mb-2">Features Enabled:</h4>
          <ul className="text-sm text-orange-700 space-y-1">
            <li>• Automatic shipping cost calculation at checkout</li>
            <li>• Create shipments when vendor dispatches orders</li>
            <li>• Real-time tracking for customers</li>
            <li>• Generate shipping labels and AWB numbers</li>
          </ul>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
          <p className="text-sm text-gray-500">
            <Info className="w-4 h-4 inline mr-1" />
            <a href="https://app.shiprocket.in/register" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">Create free Shiprocket account</a>
          </p>
        </div>
      </div>
    </IntegrationCard>
  );
}

// Intrakart WhatsApp Integration Component
function IntrakartIntegration() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [formData, setFormData] = useState({
    api_key: '',
    phone_number_id: '',
    business_account_id: '',
    webhook_verify_token: '',
    is_active: true
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/integrations/intrakart`);
      if (response.data) {
        setConfig(response.data);
        setFormData({
          api_key: response.data.api_key || '',
          phone_number_id: response.data.phone_number_id || '',
          business_account_id: response.data.business_account_id || '',
          webhook_verify_token: response.data.webhook_verify_token || '',
          is_active: response.data.is_active !== false
        });
      }
    } catch (error) {
      // Config doesn't exist yet
      if (error?.response?.status && error.response.status !== 404) {
        console.error('Analytics config fetch failed:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.api_key) {
      toast.error('Please enter the API Key');
      return;
    }

    try {
      setSaving(true);
      await axiosInstance.post(`${API_URL}/api/admin/integrations/intrakart`, formData);
      toast.success('Intrakart WhatsApp configuration saved!');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const response = await axiosInstance.post(`${API_URL}/api/admin/integrations/intrakart/test`);
      if (response.data.success) {
        toast.success('WhatsApp connection successful!');
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const getStatus = () => {
    if (!config) return 'inactive';
    if (config.is_active && config.api_key) return 'active';
    if (config.api_key) return 'configured';
    return 'inactive';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <IntegrationCard
      title="Intrakart WhatsApp"
      description="Send order updates, notifications via WhatsApp Business API"
      icon={MessageCircle}
      iconColor="bg-gradient-to-br from-green-500 to-emerald-600"
      status={getStatus()}
      onTest={config?.api_key ? handleTest : null}
      testLoading={testing}
      helpUrl="https://intrakart.com/whatsapp-business-api"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="intrakart_api_key">API Key *</Label>
            <div className="relative">
              <Input
                id="intrakart_api_key"
                type={showApiKey ? 'text' : 'password'}
                value={formData.api_key}
                onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                placeholder="Enter Intrakart API Key"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="intrakart_phone">Phone Number ID</Label>
            <Input
              id="intrakart_phone"
              value={formData.phone_number_id}
              onChange={(e) => setFormData({...formData, phone_number_id: e.target.value})}
              placeholder="WhatsApp Phone Number ID"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="intrakart_business">Business Account ID</Label>
            <Input
              id="intrakart_business"
              value={formData.business_account_id}
              onChange={(e) => setFormData({...formData, business_account_id: e.target.value})}
              placeholder="WhatsApp Business Account ID"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="intrakart_webhook">Webhook Verify Token</Label>
            <Input
              id="intrakart_webhook"
              value={formData.webhook_verify_token}
              onChange={(e) => setFormData({...formData, webhook_verify_token: e.target.value})}
              placeholder="For webhook verification"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Status</Label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm font-medium">Enable WhatsApp Notifications</span>
            </label>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-2">Notification Templates:</h4>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• Order confirmation message</li>
            <li>• Shipping update with tracking link</li>
            <li>• Delivery confirmation</li>
            <li>• Promotional messages (with consent)</li>
          </ul>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
          <p className="text-sm text-gray-500">
            <Info className="w-4 h-4 inline mr-1" />
            <a href="https://intrakart.com" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">Get Intrakart API access</a>
          </p>
        </div>
      </div>
    </IntegrationCard>
  );
}

// Shipping Rates Configuration Component (Fallback when Shiprocket is unavailable)
function ShippingRatesConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    base_rate: 50,
    per_kg_rate: 30,
    south_factor: 1.0,
    north_factor: 1.8,
    east_factor: 1.4,
    west_factor: 1.4,
    default_factor: 1.4,
    free_shipping_threshold: 0,
    is_active: true
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/shipping-config`);
      if (response.data) {
        setConfig(response.data);
        setFormData({
          base_rate: response.data.base_rate || 50,
          per_kg_rate: response.data.per_kg_rate || 30,
          south_factor: response.data.south_factor || 1.0,
          north_factor: response.data.north_factor || 1.8,
          east_factor: response.data.east_factor || 1.4,
          west_factor: response.data.west_factor || 1.4,
          default_factor: response.data.default_factor || 1.4,
          free_shipping_threshold: response.data.free_shipping_threshold || 0,
          is_active: response.data.is_active !== false
        });
      }
    } catch (error) {
      // Use defaults on error
      if (error?.response?.status && error.response.status !== 404) {
        console.error('Config load fallback triggered:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axiosInstance.post(`${API_URL}/api/admin/shipping-config`, formData);
      toast.success('Shipping rates saved!');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save shipping rates');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <IntegrationCard
      title="Fallback Shipping Rates"
      description="Configure shipping rates when Shiprocket is unavailable"
      icon={Package}
      iconColor="bg-gradient-to-br from-blue-500 to-indigo-600"
      status={formData.is_active ? 'active' : 'inactive'}
    >
      <div className="space-y-6">
        {/* Base Rates */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-3">Base Rates</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="base_rate">Base Rate (₹)</Label>
              <Input
                id="base_rate"
                type="number"
                value={formData.base_rate}
                onChange={(e) => setFormData({...formData, base_rate: parseFloat(e.target.value) || 0})}
                placeholder="50"
              />
              <p className="text-xs text-gray-500">Minimum shipping charge</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="per_kg_rate">Per KG Rate (₹)</Label>
              <Input
                id="per_kg_rate"
                type="number"
                value={formData.per_kg_rate}
                onChange={(e) => setFormData({...formData, per_kg_rate: parseFloat(e.target.value) || 0})}
                placeholder="30"
              />
              <p className="text-xs text-gray-500">Additional per kg</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="free_shipping_threshold">Free Shipping Above (₹)</Label>
              <Input
                id="free_shipping_threshold"
                type="number"
                value={formData.free_shipping_threshold}
                onChange={(e) => setFormData({...formData, free_shipping_threshold: parseFloat(e.target.value) || 0})}
                placeholder="0"
              />
              <p className="text-xs text-gray-500">0 = No free shipping</p>
            </div>
          </div>
        </div>

        {/* Distance Multipliers */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-3">Distance Multipliers</h4>
          <p className="text-sm text-gray-600 mb-3">Multiply base rate by these factors based on destination region</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label htmlFor="south_factor">South India</Label>
              <Input
                id="south_factor"
                type="number"
                step="0.1"
                value={formData.south_factor}
                onChange={(e) => setFormData({...formData, south_factor: parseFloat(e.target.value) || 1})}
                placeholder="1.0"
              />
              <p className="text-xs text-gray-500">TN, KL, KA, AP, TG</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="north_factor">North India</Label>
              <Input
                id="north_factor"
                type="number"
                step="0.1"
                value={formData.north_factor}
                onChange={(e) => setFormData({...formData, north_factor: parseFloat(e.target.value) || 1})}
                placeholder="1.8"
              />
              <p className="text-xs text-gray-500">DL, UP, PB, HR, RJ</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="east_factor">East India</Label>
              <Input
                id="east_factor"
                type="number"
                step="0.1"
                value={formData.east_factor}
                onChange={(e) => setFormData({...formData, east_factor: parseFloat(e.target.value) || 1})}
                placeholder="1.4"
              />
              <p className="text-xs text-gray-500">WB, OR, BR, JH</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="west_factor">West India</Label>
              <Input
                id="west_factor"
                type="number"
                step="0.1"
                value={formData.west_factor}
                onChange={(e) => setFormData({...formData, west_factor: parseFloat(e.target.value) || 1})}
                placeholder="1.4"
              />
              <p className="text-xs text-gray-500">MH, GJ, MP</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_factor">Other States</Label>
              <Input
                id="default_factor"
                type="number"
                step="0.1"
                value={formData.default_factor}
                onChange={(e) => setFormData({...formData, default_factor: parseFloat(e.target.value) || 1})}
                placeholder="1.4"
              />
              <p className="text-xs text-gray-500">NE, Islands, etc.</p>
            </div>
          </div>
        </div>

        {/* Formula Display */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2">Shipping Formula:</h4>
          <code className="text-sm text-blue-700 bg-blue-100 px-2 py-1 rounded">
            Shipping = (Base Rate + Weight in KG × Per KG Rate) × Distance Factor
          </code>
          <p className="text-sm text-blue-600 mt-2">
            Example: 500g to North India = (₹{formData.base_rate} + 0.5 × ₹{formData.per_kg_rate}) × {formData.north_factor} = <strong>₹{((formData.base_rate + 0.5 * formData.per_kg_rate) * formData.north_factor).toFixed(0)}</strong>
          </p>
        </div>

        <div className="space-y-2">
          <Label>Status</Label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Enable Fallback Shipping Rates</span>
            </label>
          </div>
          <p className="text-xs text-gray-500">Used when Shiprocket API is unavailable</p>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Shipping Rates
          </Button>
        </div>
      </div>
    </IntegrationCard>
  );
}

// Google Analytics Integration Component
function GoogleAnalyticsIntegration() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    measurement_id: '',
    is_active: false,
    track_pageviews: true,
    track_ecommerce: true,
    track_user_id: false
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/analytics-config`);
      const gaConfig = response.data.find(c => c.provider === 'google_analytics');
      if (gaConfig) {
        setConfig(gaConfig);
        setFormData({
          measurement_id: gaConfig.measurement_id || '',
          is_active: gaConfig.is_active || false,
          track_pageviews: gaConfig.track_pageviews !== false,
          track_ecommerce: gaConfig.track_ecommerce !== false,
          track_user_id: gaConfig.track_user_id || false
        });
      }
    } catch (error) {
      console.error('Failed to fetch GA config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.measurement_id) {
      toast.error('Please enter Measurement ID');
      return;
    }

    if (!formData.measurement_id.startsWith('G-')) {
      toast.error('Measurement ID should start with G-');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        provider: 'google_analytics',
        ...formData
      };
      
      if (config?.id) {
        await axiosInstance.put(`${API_URL}/api/admin/analytics-config/${config.id}`, payload);
      } else {
        await axiosInstance.post(`${API_URL}/api/admin/analytics-config`, payload);
      }
      
      toast.success('Google Analytics configuration saved! Refresh page to activate tracking.');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const getStatus = () => {
    if (!config) return 'inactive';
    if (config.is_active && config.measurement_id) return 'active';
    if (config.measurement_id) return 'configured';
    return 'inactive';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <IntegrationCard
      title="Google Analytics 4"
      description="Track website traffic, user behavior, and conversions"
      icon={BarChart3}
      iconColor="bg-gradient-to-br from-orange-500 to-yellow-500"
      status={getStatus()}
      helpUrl="https://support.google.com/analytics/answer/9304153"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ga_measurement_id">Measurement ID *</Label>
            <Input
              id="ga_measurement_id"
              value={formData.measurement_id}
              onChange={(e) => setFormData({...formData, measurement_id: e.target.value})}
              placeholder="G-XXXXXXXXXX"
            />
            <p className="text-xs text-gray-500">Find this in GA4 → Admin → Data Streams</p>
          </div>
        </div>

        <div className="space-y-3">
          <Label>Tracking Options</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.track_pageviews}
                onChange={(e) => setFormData({...formData, track_pageviews: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <div>
                <span className="text-sm font-medium">Page Views</span>
                <p className="text-xs text-gray-500">Track all page visits</p>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.track_ecommerce}
                onChange={(e) => setFormData({...formData, track_ecommerce: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <div>
                <span className="text-sm font-medium">E-commerce</span>
                <p className="text-xs text-gray-500">Track purchases, cart events</p>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.track_user_id}
                onChange={(e) => setFormData({...formData, track_user_id: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <div>
                <span className="text-sm font-medium">User ID</span>
                <p className="text-xs text-gray-500">Track logged-in users</p>
              </div>
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Status</Label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium">Enable Google Analytics</span>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
        </div>
      </div>
    </IntegrationCard>
  );
}

// Meta Pixel (Facebook) Integration Component
function MetaPixelIntegration() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    pixel_id: '',
    is_active: false,
    track_pageviews: true,
    track_purchases: true,
    track_add_to_cart: true,
    track_view_content: true
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/analytics-config`);
      const metaConfig = response.data.find(c => c.provider === 'meta_pixel');
      if (metaConfig) {
        setConfig(metaConfig);
        setFormData({
          pixel_id: metaConfig.pixel_id || '',
          is_active: metaConfig.is_active || false,
          track_pageviews: metaConfig.track_pageviews !== false,
          track_purchases: metaConfig.track_purchases !== false,
          track_add_to_cart: metaConfig.track_add_to_cart !== false,
          track_view_content: metaConfig.track_view_content !== false
        });
      }
    } catch (error) {
      console.error('Failed to fetch Meta Pixel config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.pixel_id) {
      toast.error('Please enter Pixel ID');
      return;
    }

    if (!/^\d+$/.test(formData.pixel_id)) {
      toast.error('Pixel ID should be numeric');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        provider: 'meta_pixel',
        ...formData
      };
      
      if (config?.id) {
        await axiosInstance.put(`${API_URL}/api/admin/analytics-config/${config.id}`, payload);
      } else {
        await axiosInstance.post(`${API_URL}/api/admin/analytics-config`, payload);
      }
      
      toast.success('Meta Pixel configuration saved! Refresh page to activate tracking.');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const getStatus = () => {
    if (!config) return 'inactive';
    if (config.is_active && config.pixel_id) return 'active';
    if (config.pixel_id) return 'configured';
    return 'inactive';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <IntegrationCard
      title="Meta Pixel (Facebook)"
      description="Track conversions and build audiences for Facebook & Instagram ads"
      icon={TrendingUp}
      iconColor="bg-gradient-to-br from-blue-600 to-indigo-600"
      status={getStatus()}
      helpUrl="https://www.facebook.com/business/help/952192354843755"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="meta_pixel_id">Pixel ID *</Label>
            <Input
              id="meta_pixel_id"
              value={formData.pixel_id}
              onChange={(e) => setFormData({...formData, pixel_id: e.target.value})}
              placeholder="123456789012345"
            />
            <p className="text-xs text-gray-500">Find this in Meta Events Manager → Data Sources</p>
          </div>
        </div>

        <div className="space-y-3">
          <Label>Events to Track</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.track_pageviews}
                onChange={(e) => setFormData({...formData, track_pageviews: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium">PageView</span>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.track_view_content}
                onChange={(e) => setFormData({...formData, track_view_content: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium">ViewContent</span>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.track_add_to_cart}
                onChange={(e) => setFormData({...formData, track_add_to_cart: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium">AddToCart</span>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.track_purchases}
                onChange={(e) => setFormData({...formData, track_purchases: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium">Purchase</span>
              </div>
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Status</Label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Enable Meta Pixel</span>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
        </div>
      </div>
    </IntegrationCard>
  );
}

// Main Component
export default function AdminIntegrations() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Integrations</h1>
          <p className="text-gray-600 mt-1">Configure payment, logistics, and communication services</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="payments" className="space-y-6">
          <TabsList className="bg-white border shadow-sm p-1 rounded-xl">
            <TabsTrigger value="payments" className="rounded-lg px-6 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <CreditCard className="w-4 h-4 mr-2" />
              Payments
            </TabsTrigger>
            <TabsTrigger value="logistics" className="rounded-lg px-6 py-2.5 data-[state=active]:bg-orange-600 data-[state=active]:text-white">
              <Truck className="w-4 h-4 mr-2" />
              Logistics
            </TabsTrigger>
            <TabsTrigger value="communication" className="rounded-lg px-6 py-2.5 data-[state=active]:bg-green-600 data-[state=active]:text-white">
              <MessageCircle className="w-4 h-4 mr-2" />
              Communication
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg px-6 py-2.5 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="payments" className="space-y-6">
            <CashfreeIntegration />
          </TabsContent>

          <TabsContent value="logistics" className="space-y-6">
            <ShiprocketIntegration />
            <ShippingRatesConfig />
          </TabsContent>

          <TabsContent value="communication" className="space-y-6">
            <IntrakartIntegration />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <GoogleAnalyticsIntegration />
            <MetaPixelIntegration />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
